import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CandidateCard, CandidateDetailModal } from "@/components/CandidateCard";
import { toast } from "sonner";
import { Loader2, Search, Send, X, Sparkles, ShieldCheck } from "lucide-react";

/**
 * The recruiter never searches freely — the platform (system) nominates
 * candidates for each open opportunity. This page renders those nominations.
 */
export default function RecruiterHome() {
  const [nominations, setNominations] = useState([]);
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ skill: "", city: "", major: "" });
  const [contactFor, setContactFor] = useState(null);
  const [detailPayload, setDetailPayload] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [n, v] = await Promise.all([api.get("/recruiter/nominations"), api.get("/role-value/recruiter")]);
      setNominations(n.data.nominations || []);
      setValue(v.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Client-side filter across the system's nominated pool
  const filtered = useMemo(() => {
    const skill = filters.skill.trim().toLowerCase();
    const city = filters.city.trim().toLowerCase();
    const major = filters.major.trim().toLowerCase();
    return nominations.filter(({ student }) => {
      const s = student || {};
      if (city && !(s.city || s.location || "").toLowerCase().includes(city)) return false;
      if (major && !(s.major || "").toLowerCase().includes(major)) return false;
      if (skill) {
        const bag = (s.competencies?.skills || []).join(" ").toLowerCase();
        if (!bag.includes(skill)) return false;
      }
      return true;
    });
  }, [nominations, filters]);

  const submitContact = async () => {
    setSending(true);
    try {
      const sid = contactFor.id || "";
      await api.post("/recruiter/contact-request", { student_id: sid, message });
      toast.success("أُرسل طلبك لإدارة المنصة — سنبلغك عند اعتماده ثم عند رد الطالب");
      setContactFor(null);
      setMessage("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="recruiter-home">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">مرشّحو النظام</div>
        <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">الخريجون الذين رشّحهم النظام لك</h1>
        <p className="mt-2 text-slate-600 max-w-3xl">
          تختار منصّة عماد الخريجين الأكثر ملاءمة لفرصك المفتوحة وتعرضهم لك هنا — دون الحاجة للبحث اليدوي. يعتمد الترشيح على تقييمات الجمعية والموجّه، الجدارات المكتسبة، والتطابق مع متطلبات الفرصة.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold px-3 py-1">
          <ShieldCheck className="h-3.5 w-3.5" />
          الترشيح يتم بواسطة النظام — لا يظهر أي طالب هنا دون اعتماد المنصّة
        </div>
      </div>

      <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="recruiter-value-summary">
        {[["مرشحون حقيقيون", value?.summary?.nominated], ["طلبات التواصل", value?.summary?.requests], ["مراجعة الإدارة", value?.summary?.pending_admin], ["موافقة الطالب", value?.summary?.student_review], ["موافق عليها", value?.summary?.approved]].map(([label,count]) => <div key={label} className="rounded-2xl border bg-white p-4"><b className="block text-2xl text-teal-800">{count || 0}</b><span className="text-xs text-slate-500">{label}</span></div>)}
      </section>
      {/* Filter — narrows the system pool, does not replace it */}
      <div className="mb-8 grid sm:grid-cols-4 gap-3 p-4 bg-white rounded-2xl border border-cream-200 card-shadow" data-testid="recruiter-filters">
        <input value={filters.skill} onChange={(e) => setFilters({ ...filters, skill: e.target.value })} placeholder="مهارة (مثال: Python)" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="filter-skill" />
        <input value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder="المدينة" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="filter-city" />
        <input value={filters.major} onChange={(e) => setFilters({ ...filters, major: e.target.value })} placeholder="التخصص" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="filter-major" />
        <button onClick={() => setFilters({ ...filters })} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600" data-testid="filter-apply">
          <Search className="h-4 w-4" /> بحث
        </button>
      </div>

      <section data-testid="nominations-section">
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700 inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> مرشّحون مميّزون
            </div>
            <h2 className="mt-1 text-xl font-heading font-black text-slate-900">
              {filtered.length > 0 ? `${filtered.length} خرّيج مرشّح لفرصك المفتوحة` : "لا توجد ترشيحات مطابقة"}
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-600" data-testid="empty-nominations">
            لم يرشّح النظام أي خرّيج مطابق لهذه المعايير الآن. جرّب تعديل الفلاتر أو انتظر التحديث التالي.
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="nominations-grid">
            {filtered.map(({ student, opportunity }) => (
              <CandidateCard
                key={student.id}
                student={student}
                opportunity={opportunity}
                onOpen={(s, o) => setDetailPayload({ student: s, opportunity: o })}
                onContact={setContactFor}
                ctaLabel="طلب تواصل"
              />
            ))}
          </div>
        )}
      </section>

      <CandidateDetailModal
        payload={detailPayload}
        onClose={() => setDetailPayload(null)}
        actionSlot={
          detailPayload && (
            <>
              <button
                onClick={() => { setContactFor(detailPayload.student); setDetailPayload(null); }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg bg-teal-700 text-white font-bold hover:bg-teal-600 transition-colors"
                data-testid={`modal-contact-${detailPayload.student.id}`}
              >
                <Send className="h-4 w-4" /> طلب تواصل مع {detailPayload.student.name.split(" ")[0]}
              </button>
              <button onClick={() => setDetailPayload(null)} className="px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-100">إغلاق</button>
            </>
          )
        }
      />

      {contactFor && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) setContactFor(null); }}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="contact-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="font-heading font-bold text-slate-900 text-lg">طلب تواصل — {contactFor.name}</h3>
              <button onClick={() => setContactFor(null)} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="إغلاق"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-slate-600">سيصل طلبك إلى <strong>إدارة المنصة</strong> أولاً للمراجعة، ثم تُحيله للطالب لأخذ موافقته. لا تظهر بيانات التواصل إلا بعد قبول الطالب صراحةً. تابع الحالة من صفحة «طلبات التواصل».</p>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="عرّف بجهتك واذكر سبب طلب التواصل..." className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="contact-msg" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setContactFor(null)} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
                <button onClick={submitContact} disabled={sending} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid="contact-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال الطلب
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
